/**
 * Wave chat-instant · ChatPanel
 *
 * Drawer lateral (desktop) ou bottom sheet (mobile) com TODA a UX de
 * chat dentro: lista de conversations + thread INLINE quando uma é
 * aberta. JP foi explícito (2026-05-21): clicar numa conversation NÃO
 * pode abrir uma janela separada por trás do panel; a thread tem que
 * substituir a lista no mesmo container, com botão "← Voltar" no header.
 *
 * Visual SEPARADO do inbox:
 *   - Cards com avatar grande + dot de presence verde se online
 *   - Preview da última mensagem (sem subject)
 *   - Unread badge brand-500 (não vermelho)
 *   - Timestamp relativo curto (5m, 2h, ontem)
 *   - "Nova conversa" → busca user e abre DM INLINE no mesmo panel
 *
 * Stack interno:
 *   panelView === 'list'   → header + busca + tabs + lista de convs
 *   panelView === 'thread' → header com back + thread (bubbles + composer)
 *
 * Transição: 220ms cubic-bezier(0.32,0.72,0,1) slide-left ao entrar na
 * thread, slide-right reverso ao voltar. Mesma curva do panel próprio
 * pra parecer um único movimento contínuo.
 *
 * Mobile bottom sheet: animate translate-y-full → 0; desktop drawer da
 * direita com translateX(12px) → 0.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '@/store/auth.store';
import { useChatInstantStore } from '@/store/chat-instant.store';
import { chatInstantApi } from '@/api/chat-instant';
import type { ChatInstantMessage } from '@/api/chat-instant';
import { api } from '@/api/client';
import { useT } from '@/hooks/useT';
import { cn } from '@/lib/cn';
// Wave chat-identity-fix · 2026-05-26 — usa JWT.sub como autoridade pra
// "é meu?" no rendering de bubbles, ao invés de me.id persistido (que
// pode estar stale no localStorage do mobile). Veja lib/auth-identity.ts.
import { useAuthoritativeUserId } from '@/lib/auth-identity';
import { parseServerDate } from '@/lib/parse-server-date';

type Tab = 'all' | 'unread' | 'groups';

const LIST_POLL_MS = 5_000;
const THREAD_POLL_MS = 3_000;
const PRESENCE_PING_MS = 25_000;
const TYPING_DEBOUNCE_MS = 1_500;

export function ChatPanel() {
  const { t, locale } = useT();
  const isEn = locale === 'en';
  const me = useAuth((s) => s.user);
  // Wave chat-identity-fix · id autoritativo. Bubble "minha" só renderiza
  // certa quando comparamos contra JWT.sub (mesma fonte que o backend usa
  // pra gravar author_user_id).
  const meId = useAuthoritativeUserId();
  const open = useChatInstantStore((s) => s.panelOpen);
  const closePanel = useChatInstantStore((s) => s.closePanel);
  const conversations = useChatInstantStore((s) => s.conversations);
  const setConversations = useChatInstantStore((s) => s.setConversations);
  const panelView = useChatInstantStore((s) => s.panelView);
  const activeConversationId = useChatInstantStore((s) => s.panelActiveConversationId);
  const openThreadInPanel = useChatInstantStore((s) => s.openThreadInPanel);
  const backToList = useChatInstantStore((s) => s.backToList);

  const [tab, setTab] = useState<Tab>('all');
  const [query, setQuery] = useState('');
  const [loadingFirst, setLoadingFirst] = useState(false);
  const [newOpen, setNewOpen] = useState(false);

  // Esc fecha o panel (ou volta pra lista se estiver na thread).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (panelView === 'thread') {
        backToList();
      } else {
        closePanel();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, panelView, backToList, closePanel]);

  // Polling da lista enquanto o panel está aberto (qualquer view).
  useEffect(() => {
    if (!open || !me) return;
    let alive = true;
    const tick = async () => {
      try {
        const list = await chatInstantApi.listConversations();
        if (alive) setConversations(list);
      } catch {
        // silencioso
      }
    };
    if (conversations.length === 0) setLoadingFirst(true);
    void tick().finally(() => alive && setLoadingFirst(false));
    const id = window.setInterval(tick, LIST_POLL_MS);
    return () => {
      alive = false;
      window.clearInterval(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, me]);

  // Filtra por tab + query (case-insensitive em nome do peer / nome do grupo).
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return conversations.filter((c) => {
      if (tab === 'unread' && c.unreadCount === 0) return false;
      if (tab === 'groups' && c.type !== 'group') return false;
      if (!q) return true;
      const peerName = peerNameOf(c, meId ?? undefined, isEn);
      const groupName = c.name ?? '';
      return (
        peerName.toLowerCase().includes(q) ||
        groupName.toLowerCase().includes(q)
      );
    });
  }, [conversations, tab, query, meId, isEn]);

  // Wave chat-identity-fix · guarda também por meId: sem identidade
  // confiável, não renderiza chat (evita bubble do lado errado).
  if (!open || !me || !meId) return null;

  const openDM = async (peerUserId: string) => {
    try {
      const { conversationId } = await chatInstantApi.openDM(peerUserId);
      // Abre a thread INLINE (substituindo a lista). NÃO usa openWindow
      // pra evitar o bug "janela atrás do panel" que JP reportou.
      openThreadInPanel(conversationId);
      setNewOpen(false);
      // Refresh imediato pra panel mostrar a conv nova no topo da lista
      // quando o user voltar.
      const list = await chatInstantApi.listConversations();
      setConversations(list);
    } catch {
      // ApiError com mensagem humana já é showed via bounce; aqui swallow.
    }
  };

  const activeConv = activeConversationId
    ? conversations.find((c) => c.id === activeConversationId) ?? null
    : null;

  return (
    <>
      {/* Backdrop — semi-transparent overlay sobre toda a tela em todos os
          breakpoints. z-[59] fica logo abaixo do panel (z-[60]) e acima
          da chrome (z-50). */}
      <div
        data-testid="chat-instant-panel-backdrop"
        onClick={closePanel}
        className={cn(
          'fixed inset-0 z-[59] bg-ink/40 backdrop-blur-sm',
          'animate-fade-in',
        )}
        aria-hidden="true"
      />

      <aside
        role="dialog"
        aria-label={t.chatInstant.panel.title}
        data-testid="chat-instant-panel"
        className={cn(
          'fixed z-[60] flex flex-col bg-surface-2 border-line shadow-pop dark:shadow-pop-dark',
          // Mobile: bottom sheet ancorado no rodapé, abaixo da TopBar (h-14).
          'inset-x-0 bottom-0 top-14 rounded-t-2xl border-t',
          // Desktop: drawer lateral direita, abaixo da TopBar (h-16=64px),
          // com 12px de respiro. Width fixo 420px.
          'md:inset-x-auto md:left-auto md:top-[72px] md:right-3 md:bottom-3',
          'md:w-[min(420px,calc(100vw-1rem))] md:rounded-2xl md:border md:rounded-t-2xl',
          // Animação de entrada do panel.
          'animate-[panelUp_220ms_cubic-bezier(0.32,0.72,0,1)_both]',
          'md:animate-[panelInRight_220ms_cubic-bezier(0.32,0.72,0,1)_both]',
          'pb-[env(safe-area-inset-bottom)]',
          'overflow-hidden',
        )}
      >
        <style>{`
          @keyframes panelUp {
            0% { transform: translateY(8%); opacity: 0; }
            100% { transform: translateY(0); opacity: 1; }
          }
          @keyframes panelInRight {
            0% { transform: translateX(12px); opacity: 0; }
            100% { transform: translateX(0); opacity: 1; }
          }
          @keyframes panelStackSlideInRight {
            0% { transform: translateX(8%); opacity: 0; }
            100% { transform: translateX(0); opacity: 1; }
          }
          @keyframes panelStackSlideInLeft {
            0% { transform: translateX(-8%); opacity: 0; }
            100% { transform: translateX(0); opacity: 1; }
          }
        `}</style>

        {panelView === 'list' && (
          <ListView
            t={t}
            isEn={isEn}
            meId={meId}
            tab={tab}
            setTab={setTab}
            query={query}
            setQuery={setQuery}
            newOpen={newOpen}
            setNewOpen={setNewOpen}
            loadingFirst={loadingFirst}
            filtered={filtered}
            onClosePanel={closePanel}
            onOpenDM={openDM}
            onPickConv={(convId) => openThreadInPanel(convId)}
          />
        )}

        {panelView === 'thread' && activeConv && (
          <ThreadView
            key={activeConv.id}
            isEn={isEn}
            meId={meId}
            conversationId={activeConv.id}
            onBack={backToList}
            onClose={closePanel}
          />
        )}

        {panelView === 'thread' && !activeConv && (
          // Conv ainda não chegou no cache (raro — listConversations
          // resolve em ms). Mostra placeholder skeleton ao invés de
          // empurrar o user de volta sem aviso.
          <div className="flex-1 flex items-center justify-center px-6 py-10">
            <p className="text-xs text-ink-subtle">{t.common.loading}</p>
          </div>
        )}
      </aside>
    </>
  );
}

// ── List view (default) ──────────────────────────────────────────────

interface ListViewProps {
  t: ReturnType<typeof useT>['t'];
  isEn: boolean;
  meId: string;
  tab: Tab;
  setTab: (t: Tab) => void;
  query: string;
  setQuery: (s: string) => void;
  newOpen: boolean;
  setNewOpen: (b: boolean | ((v: boolean) => boolean)) => void;
  loadingFirst: boolean;
  filtered: ReturnType<typeof useChatInstantStore.getState>['conversations'];
  onClosePanel: () => void;
  onOpenDM: (peerUserId: string) => void;
  onPickConv: (convId: string) => void;
}

function ListView({
  t,
  isEn,
  meId,
  tab,
  setTab,
  query,
  setQuery,
  newOpen,
  setNewOpen,
  loadingFirst,
  filtered,
  onClosePanel,
  onOpenDM,
  onPickConv,
}: ListViewProps) {
  return (
    <div
      data-testid="chat-instant-panel-list"
      className={cn(
        'flex flex-1 min-h-0 flex-col',
        // slide da esquerda quando volta da thread (sutil)
        'animate-[panelStackSlideInLeft_220ms_cubic-bezier(0.32,0.72,0,1)_both]',
      )}
    >
      {/* Header */}
      <div className="border-b border-line px-4 pt-4 pb-3">
        <div className="flex items-start justify-between gap-2">
          <div>
            <h2 className="text-base font-semibold text-ink">
              {t.chatInstant.panel.title}
            </h2>
            <p className="text-[11px] text-ink-subtle mt-0.5">
              {t.chatInstant.panel.subtitle}
            </p>
          </div>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => setNewOpen((v) => !v)}
              className={cn(
                'pressable focus-ring inline-flex h-11 items-center gap-1.5 rounded-full',
                'bg-brand-500 px-4 text-xs font-semibold text-white',
                'hover:bg-brand-600 transition-colors duration-150',
              )}
              aria-label={t.chatInstant.panel.newButton}
            >
              <svg className="h-3.5 w-3.5" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
              </svg>
              {t.chatInstant.panel.newButton}
            </button>
            <button
              type="button"
              onClick={onClosePanel}
              aria-label={t.common.close}
              className={cn(
                'pressable focus-ring flex h-11 w-11 items-center justify-center rounded-full',
                'text-ink-muted hover:bg-surface-3 hover:text-ink transition-colors duration-150',
              )}
            >
              <svg className="h-4 w-4" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </button>
          </div>
        </div>

        {/* Search local */}
        <div className="mt-3 relative">
          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-ink-subtle"
            viewBox="0 0 16 16"
            fill="none"
            aria-hidden="true"
          >
            <circle cx="7" cy="7" r="4.5" stroke="currentColor" strokeWidth="1.4" />
            <path d="M11 11l3 3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
          </svg>
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t.chatInstant.panel.searchPlaceholder}
            className={cn(
              'w-full rounded-full border border-line bg-surface',
              'pl-8 pr-3 py-2 text-xs text-ink placeholder:text-ink-subtle',
              'focus:outline-none focus:border-line-strong transition-colors duration-150',
            )}
          />
        </div>

        {/* Tabs */}
        <div className="mt-3 flex items-center gap-1">
          {(['all', 'unread', 'groups'] as Tab[]).map((tb) => (
            <button
              key={tb}
              type="button"
              onClick={() => setTab(tb)}
              className={cn(
                'pressable rounded-full px-3 py-1 text-[11px] font-medium transition-colors duration-150',
                tab === tb
                  ? 'bg-ink text-surface'
                  : 'text-ink-muted hover:bg-surface-3 hover:text-ink',
              )}
            >
              {tb === 'all'
                ? t.chatInstant.panel.tabs.all
                : tb === 'unread'
                  ? t.chatInstant.panel.tabs.unread
                  : t.chatInstant.panel.tabs.groups}
            </button>
          ))}
        </div>
      </div>

      {/* New chat picker (autocomplete) */}
      {newOpen && (
        <NewChatPicker
          onPick={onOpenDM}
          onClose={() => setNewOpen(false)}
        />
      )}

      {/* List */}
      <div className="flex-1 overflow-y-auto">
        {loadingFirst && (
          <div className="px-4 py-4 space-y-3">
            {[0, 1, 2].map((i) => (
              <div key={i} className="flex items-center gap-3 animate-pulse">
                <div className="h-10 w-10 rounded-full bg-surface-3" />
                <div className="flex-1 space-y-1.5">
                  <div className="h-3 w-1/2 rounded bg-surface-3" />
                  <div className="h-2.5 w-3/4 rounded bg-surface-3" />
                </div>
              </div>
            ))}
          </div>
        )}

        {!loadingFirst && filtered.length === 0 && (
          <div className="flex h-full flex-col items-center justify-center px-6 py-10 text-center">
            <div
              aria-hidden="true"
              className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-brand-500/10 text-brand-500"
            >
              <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none">
                <path
                  d="M4 6.5C4 5.67 4.67 5 5.5 5h13c.83 0 1.5.67 1.5 1.5v9c0 .83-.67 1.5-1.5 1.5H10.5l-3.7 3.7c-.31.31-.85.09-.85-.35V17H5.5c-.83 0-1.5-.67-1.5-1.5v-9Z"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <p className="text-sm font-medium text-ink">
              {query
                ? t.chatInstant.panel.emptySearch
                : tab === 'unread'
                  ? t.chatInstant.panel.emptyUnread
                  : t.chatInstant.panel.empty}
            </p>
            {!query && tab !== 'unread' && (
              <button
                type="button"
                onClick={() => setNewOpen(true)}
                className={cn(
                  'mt-3 pressable focus-ring rounded-full bg-brand-500 px-4 text-xs font-semibold text-white',
                  'min-h-[44px] inline-flex items-center justify-center',
                  'hover:bg-brand-600 transition-colors duration-150',
                )}
              >
                {t.chatInstant.panel.emptyCta}
              </button>
            )}
          </div>
        )}

        {!loadingFirst &&
          filtered.map((conv, i) => (
            <ConversationCard
              key={conv.id}
              conversation={conv}
              meId={meId}
              isEn={isEn}
              onClick={() => onPickConv(conv.id)}
              style={{ animationDelay: `${Math.min(i * 30, 180)}ms` }}
            />
          ))}
      </div>
    </div>
  );
}

// ── Conversation card (lista) ────────────────────────────────────────

function ConversationCard({
  conversation,
  meId,
  isEn,
  onClick,
  style,
}: {
  conversation: ReturnType<typeof useChatInstantStore.getState>['conversations'][number];
  meId: string;
  isEn: boolean;
  onClick: () => void;
  style?: React.CSSProperties;
}) {
  const peerName = peerNameOf(conversation, meId, isEn);
  const avatarSrc =
    conversation.type === 'dm'
      ? conversation.members.find((m) => m.userId !== meId)?.avatarUrl ?? null
      : null;
  const initials = peerName
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
  const preview = conversation.lastMessage?.body ?? '';
  const previewIsMine = conversation.lastMessage?.authorUserId === meId;
  const when = conversation.lastMessageAt
    ? relativeShort(conversation.lastMessageAt, isEn)
    : '';
  const online = !!conversation.peerOnline;

  return (
    <button
      type="button"
      data-testid="chat-instant-conv-card"
      onClick={onClick}
      style={style}
      className={cn(
        'group flex w-full items-center gap-3 border-b border-line/60 px-4 py-3 text-left',
        'hover:bg-surface-3 transition-colors duration-120',
        'animate-fade-in-up',
        'min-h-[64px]',
      )}
    >
      {/* Avatar + presence dot */}
      <div className="relative shrink-0">
        {avatarSrc ? (
          <img
            src={avatarSrc}
            alt={peerName}
            className="h-10 w-10 rounded-full object-cover"
          />
        ) : (
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-500/15 text-xs font-semibold text-brand-600">
            {initials || '?'}
          </div>
        )}
        {conversation.type === 'dm' && (
          <span
            aria-label={online ? 'Online' : 'Offline'}
            className={cn(
              'absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full ring-2 ring-surface-2',
              online ? 'bg-ok' : 'bg-ink-soft',
            )}
          />
        )}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <span
            className={cn(
              'truncate text-sm',
              conversation.unreadCount > 0 ? 'font-semibold text-ink' : 'text-ink',
            )}
          >
            {peerName}
          </span>
          {when && (
            <span className="ml-auto shrink-0 text-[10px] text-ink-subtle">
              {when}
            </span>
          )}
        </div>
        <div className="mt-0.5 flex items-center gap-1.5">
          <p
            className={cn(
              'truncate text-[12px] flex-1',
              conversation.unreadCount > 0 ? 'text-ink' : 'text-ink-muted',
            )}
          >
            {previewIsMine && preview ? (isEn ? 'You: ' : 'Você: ') : ''}
            {preview || '—'}
          </p>
          {conversation.unreadCount > 0 && (
            <span
              data-testid="chat-instant-unread-badge"
              className="shrink-0 inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-brand-500 px-1 text-[10px] font-semibold text-white"
            >
              {conversation.unreadCount > 99 ? '99+' : conversation.unreadCount}
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

// ── New chat picker ───────────────────────────────────────────────────

function NewChatPicker({
  onPick,
  onClose,
}: {
  onPick: (userId: string) => void;
  onClose: () => void;
}) {
  const { t } = useT();
  const [q, setQ] = useState('');
  const [results, setResults] = useState<
    Array<{ id: string; fullName: string; avatarUrl: string | null }>
  >([]);
  const [searching, setSearching] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const debounceRef = useRef<number | null>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    if (q.trim().length < 2) {
      setResults([]);
      return;
    }
    setSearching(true);
    debounceRef.current = window.setTimeout(async () => {
      try {
        const r = await api.searchUsers(q.trim());
        setResults(r.map((u) => ({ id: u.id, fullName: u.fullName, avatarUrl: u.avatarUrl })));
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 220);
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [q]);

  return (
    <div className="border-b border-line bg-surface px-4 py-3 animate-fade-in-down">
      <div className="flex items-center gap-2">
        <input
          ref={inputRef}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t.chatInstant.panel.newSearchPlaceholder}
          className={cn(
            'flex-1 rounded-full border border-line bg-surface-2',
            'px-3 py-1.5 text-xs text-ink placeholder:text-ink-subtle',
            'focus:outline-none focus:border-line-strong',
          )}
        />
        <button
          type="button"
          onClick={onClose}
          className="pressable text-xs text-ink-muted hover:text-ink"
        >
          {t.common.cancel}
        </button>
      </div>

      {searching && (
        <p className="mt-2 text-[11px] text-ink-subtle">{t.common.loading}</p>
      )}

      {!searching && q.trim().length >= 2 && results.length === 0 && (
        <p className="mt-2 text-[11px] text-ink-subtle">
          {t.chatInstant.panel.newNoResults}
        </p>
      )}

      {results.length > 0 && (
        <ul className="mt-2 max-h-48 overflow-y-auto space-y-1">
          {results.map((u) => (
            <li key={u.id}>
              <button
                type="button"
                onClick={() => onPick(u.id)}
                className={cn(
                  'pressable flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs',
                  'text-ink hover:bg-surface-3 transition-colors duration-120',
                )}
              >
                {u.avatarUrl ? (
                  <img src={u.avatarUrl} alt={u.fullName} className="h-6 w-6 rounded-full object-cover" />
                ) : (
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-brand-500/15 text-[10px] font-semibold text-brand-600">
                    {u.fullName.slice(0, 1)}
                  </span>
                )}
                <span className="truncate">{u.fullName}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── Thread view (inline no panel) ────────────────────────────────────

/**
 * View da thread ativa, renderizada DENTRO do mesmo container do panel.
 * Substitui a ListView quando o user clica numa conversation. Tem
 * polling próprio de mensagens + presence, e fecha pelo back ou X.
 *
 * Por que não reaproveitar o componente ChatWindow? Porque ele tem suas
 * próprias âncoras de posição (fixed bottom-right desktop / full-screen
 * mobile) e seu próprio z-index. Aqui queremos um filho flex normal do
 * panel. Encapsulamos a lógica de polling/send/auto-scroll localmente.
 */
function ThreadView({
  isEn,
  meId,
  conversationId,
  onBack,
  onClose,
}: {
  isEn: boolean;
  meId: string;
  conversationId: string;
  onBack: () => void;
  onClose: () => void;
}) {
  const { t } = useT();
  const conv = useChatInstantStore((s) =>
    s.conversations.find((c) => c.id === conversationId),
  );
  const messages = useChatInstantStore(
    (s) => s.messagesByConv.get(conversationId) ?? [],
  );
  const presenceMap = useChatInstantStore((s) => s.presence);
  const setMessages = useChatInstantStore((s) => s.setMessages);
  const appendMessage = useChatInstantStore((s) => s.appendMessage);
  const removeMessage = useChatInstantStore((s) => s.removeMessage);
  const decrementUnread = useChatInstantStore((s) => s.decrementUnread);
  const setPresence = useChatInstantStore((s) => s.setPresence);
  // Wave chat-pills · 2026-05-21 — sender bumpa pill on every send.
  const pinConversation = useChatInstantStore((s) => s.pinConversation);
  const markPillRead = useChatInstantStore((s) => s.markPillRead);

  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const typingTimerRef = useRef<number | null>(null);
  const lastTypingPingRef = useRef<number>(0);
  const prevMsgCountRef = useRef<number>(0);

  // Poll mensagens enquanto a thread tá visível.
  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        const msgs = await chatInstantApi.getMessages(conversationId, { limit: 80 });
        if (alive) setMessages(conversationId, msgs);
      } catch {
        // silencioso
      }
    };
    void tick();
    const id = window.setInterval(tick, THREAD_POLL_MS);
    return () => {
      alive = false;
      window.clearInterval(id);
    };
  }, [conversationId, setMessages]);

  // Marca read ao abrir / quando chega msg.
  useEffect(() => {
    if (!conv || conv.unreadCount === 0) return;
    void chatInstantApi
      .markRead(conversationId)
      .then(() => {
        decrementUnread(conversationId);
        // Pill: zera badge do pill da mesma conv (se existir).
        markPillRead(conversationId);
      })
      .catch(() => {});
  }, [conv, conversationId, decrementUnread, markPillRead]);

  // Zera badge do pill sempre que essa thread está aberta no panel —
  // não só na transição inicial. Cobre o caso de novas msgs chegarem
  // enquanto o user já tá olhando.
  useEffect(() => {
    markPillRead(conversationId);
  }, [conversationId, markPillRead]);

  // Presence heartbeat + lookup do peer.
  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        await chatInstantApi.ping(null);
      } catch {}
      if (!alive) return;
      if (conv && conv.type === 'dm') {
        const peer = conv.members.find((m) => m.userId !== meId);
        if (peer) {
          try {
            const p = await chatInstantApi.getPresence([peer.userId]);
            if (alive) setPresence(p);
          } catch {}
        }
      }
    };
    void tick();
    const id = window.setInterval(tick, PRESENCE_PING_MS);
    return () => {
      alive = false;
      window.clearInterval(id);
    };
  }, [conv, meId, setPresence]);

  // Auto-scroll bottom on new msg.
  useEffect(() => {
    if (messages.length > prevMsgCountRef.current) {
      const el = scrollRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    }
    prevMsgCountRef.current = messages.length;
  }, [messages.length]);

  // Focus composer ao montar (desktop).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.innerWidth >= 768) {
      const id = window.setTimeout(() => inputRef.current?.focus(), 240);
      return () => window.clearTimeout(id);
    }
    return undefined;
  }, []);

  const notifyTyping = () => {
    const now = Date.now();
    if (now - lastTypingPingRef.current > 2_000) {
      lastTypingPingRef.current = now;
      void chatInstantApi.ping(conversationId).catch(() => {});
    }
    if (typingTimerRef.current) window.clearTimeout(typingTimerRef.current);
    typingTimerRef.current = window.setTimeout(() => {
      void chatInstantApi.ping(null).catch(() => {});
    }, TYPING_DEBOUNCE_MS);
  };

  const send = async () => {
    const text = body.trim();
    if (!text || sending) return;
    setSending(true);
    const tempId = `temp-${Date.now()}`;
    const tempMsg: ChatInstantMessage = {
      id: tempId,
      conversationId,
      conversationType: conv?.type ?? 'dm',
      authorUserId: meId,
      body: text,
      replyToId: null,
      createdAt: new Date().toISOString(),
      expiresAt: new Date(Date.now() + 7 * 86_400_000).toISOString(),
      deletedAt: null,
      reactions: [],
    };
    appendMessage(conversationId, tempMsg);
    setBody('');
    try {
      const real = await chatInstantApi.sendMessage(conversationId, text);
      removeMessage(conversationId, tempId);
      appendMessage(conversationId, real);
      // Auto-pin: o user mandou — essa conv ganha (ou renova) pill por 30min.
      // incomingUnread=false porque é a própria pessoa enviando.
      pinConversation(conversationId);
    } catch {
      removeMessage(conversationId, tempId);
      setBody(text);
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void send();
    } else {
      notifyTyping();
    }
  };

  // Peer info
  const peerName = useMemo(() => {
    if (!conv) return '';
    if (conv.type === 'group') return conv.name ?? (isEn ? 'Group' : 'Grupo');
    const p = conv.members.find((m) => m.userId !== meId);
    return p?.fullName ?? (isEn ? '(you)' : '(você)');
  }, [conv, meId, isEn]);

  const peerId = useMemo(() => {
    if (!conv || conv.type !== 'dm') return null;
    return conv.members.find((m) => m.userId !== meId)?.userId ?? null;
  }, [conv, meId]);

  const peerAvatar =
    conv?.type === 'dm'
      ? conv.members.find((m) => m.userId !== meId)?.avatarUrl ?? null
      : null;
  const peerPresence = peerId ? presenceMap.get(peerId) : undefined;
  const peerOnline = peerPresence?.online ?? conv?.peerOnline ?? false;
  const peerTyping = peerPresence?.isTypingIn === conversationId;
  const subtitle = peerTyping
    ? t.chatInstant.window.typing(peerName)
    : peerOnline
      ? t.chatInstant.window.online
      : peerPresence?.lastSeenAt
        ? t.chatInstant.window.offline(relativeShort(peerPresence.lastSeenAt, isEn))
        : '';

  const initials = peerName
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');

  return (
    <div
      data-testid="chat-instant-panel-thread"
      className={cn(
        'flex flex-1 min-h-0 flex-col',
        'animate-[panelStackSlideInRight_220ms_cubic-bezier(0.32,0.72,0,1)_both]',
      )}
    >
      {/* Header: ← back + avatar + nome/status + X close */}
      <header className="flex items-center gap-2 border-b border-line bg-surface-2 px-2 py-2.5">
        <button
          type="button"
          onClick={onBack}
          aria-label={t.common.back}
          data-testid="chat-instant-panel-thread-back"
          className={cn(
            'pressable focus-ring flex h-11 w-11 shrink-0 items-center justify-center rounded-full',
            'text-ink-muted hover:bg-surface-3 hover:text-ink transition-colors duration-150',
          )}
        >
          <svg className="h-5 w-5" viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <path d="M12 4l-6 6 6 6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>

        <div className="relative shrink-0">
          {peerAvatar ? (
            <img
              src={peerAvatar}
              alt={peerName}
              className="h-9 w-9 rounded-full object-cover"
            />
          ) : (
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-500/15 text-[11px] font-semibold text-brand-600">
              {initials || '?'}
            </div>
          )}
          {conv?.type === 'dm' && (
            <span
              aria-label={peerOnline ? 'Online' : 'Offline'}
              className={cn(
                'absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full ring-2 ring-surface-2',
                peerOnline ? 'bg-ok' : 'bg-ink-soft',
              )}
            />
          )}
        </div>

        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-ink leading-tight">
            {peerName}
          </p>
          {subtitle && (
            <p
              data-testid="chat-instant-panel-thread-subtitle"
              className="truncate text-[11px] text-ink-subtle leading-tight"
            >
              {subtitle}
            </p>
          )}
        </div>

        <button
          type="button"
          onClick={onClose}
          aria-label={t.common.close}
          className={cn(
            'pressable focus-ring flex h-11 w-11 shrink-0 items-center justify-center rounded-full',
            'text-ink-muted hover:bg-surface-3 hover:text-ink transition-colors duration-150',
          )}
        >
          <svg className="h-4 w-4" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </button>
      </header>

      {/* Body — bubbles */}
      <div
        ref={scrollRef}
        data-testid="chat-instant-panel-thread-scroll"
        className="flex-1 overflow-y-auto px-3 py-3 space-y-1 bg-surface"
      >
        {messages.length === 0 && (
          <div className="flex h-full flex-col items-center justify-center px-6 text-center">
            <div
              aria-hidden="true"
              className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-brand-500/10 text-brand-500"
            >
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none">
                <path
                  d="M4 6.5C4 5.67 4.67 5 5.5 5h13c.83 0 1.5.67 1.5 1.5v9c0 .83-.67 1.5-1.5 1.5H10.5l-3.7 3.7c-.31.31-.85.09-.85-.35V17H5.5c-.83 0-1.5-.67-1.5-1.5v-9Z"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <p className="text-xs text-ink-muted leading-relaxed">
              {t.chatInstant.window.empty}
            </p>
            <p className="mt-1 text-[10px] text-ink-subtle">
              {t.chatInstant.panel.subtitle}
            </p>
          </div>
        )}

        {messages.length > 0 && renderInlineBubbles(messages, meId, isEn)}
      </div>

      {/* Composer */}
      <div className="border-t border-line bg-surface-2 px-2 py-2">
        <div className="flex items-end gap-1.5">
          <textarea
            ref={inputRef}
            rows={1}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={t.chatInstant.window.placeholder}
            disabled={sending}
            className={cn(
              'flex-1 resize-none rounded-2xl border border-line bg-surface',
              'px-3 py-2 text-sm text-ink placeholder:text-ink-subtle',
              'focus:outline-none focus:border-line-strong transition-colors duration-120',
              'min-h-[44px] max-h-[120px]',
            )}
            style={{ overflow: 'hidden' }}
          />
          <button
            type="button"
            onClick={() => void send()}
            disabled={!body.trim() || sending}
            aria-label={t.chatInstant.window.send}
            className={cn(
              'pressable shrink-0 flex h-11 w-11 items-center justify-center rounded-full',
              'transition-colors duration-120',
              body.trim() && !sending
                ? 'bg-brand-500 text-white hover:bg-brand-600'
                : 'bg-surface-3 text-ink-soft cursor-not-allowed',
            )}
          >
            <svg className="h-4 w-4" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="M2 8l12-6-4 14-3-6-5-2Z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" fill="none" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Bubble renderer (inline, sem reactions/picker — kept lean) ────────

function renderInlineBubbles(messages: ChatInstantMessage[], meId: string, isEn: boolean) {
  const nodes: React.ReactNode[] = [];
  let lastTimestamp: number | null = null;

  messages.forEach((m) => {
    const isMine = m.authorUserId === meId;
    const ts = new Date(m.createdAt).getTime();
    if (lastTimestamp === null || ts - lastTimestamp > 5 * 60_000) {
      nodes.push(
        <div
          key={`ts-${m.id}`}
          className="text-center text-[10px] text-ink-subtle py-2 select-none"
        >
          {formatGroupTimestamp(m.createdAt, isEn)}
        </div>,
      );
      lastTimestamp = ts;
    }
    nodes.push(
      <div
        key={m.id}
        className={cn(
          'flex items-end gap-1.5 mb-1',
          isMine ? 'justify-end' : 'justify-start',
        )}
      >
        <div className={cn('max-w-[80%] animate-fade-in-up')}>
          <div
            className={cn(
              'px-3 py-2 text-sm leading-snug rounded-2xl break-words whitespace-pre-wrap',
              isMine
                ? 'bg-brand-500/15 text-ink rounded-tr-md'
                : 'bg-surface-3 text-ink rounded-tl-md',
            )}
          >
            {m.body}
          </div>
          {m.reactions.length > 0 && (
            <div
              className={cn(
                'mt-1 flex flex-wrap gap-1',
                isMine ? 'justify-end' : 'justify-start',
              )}
            >
              {m.reactions.map((r) => (
                <span
                  key={r.emoji}
                  className={cn(
                    'inline-flex items-center gap-1 rounded-full border border-line bg-surface-2',
                    'px-1.5 py-0.5 text-[11px] text-ink-muted',
                  )}
                >
                  <span aria-hidden>{r.emoji}</span>
                  <span className="tabular-nums">{r.users.length}</span>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>,
    );
  });

  return nodes;
}

// ── Helpers ──────────────────────────────────────────────────────────

function peerNameOf(
  conv: ReturnType<typeof useChatInstantStore.getState>['conversations'][number],
  meId: string | undefined,
  isEn: boolean,
): string {
  if (conv.type === 'group') return conv.name ?? (isEn ? 'Group' : 'Grupo');
  const peer = conv.members.find((m) => m.userId !== meId);
  return peer?.fullName ?? (isEn ? '(you)' : '(você)');
}

function relativeShort(iso: string | null | undefined, isEn: boolean): string {
  if (!iso) return '';
  const date = parseServerDate(iso);
  if (!date) return '';
  const t = date.getTime();
  const diff = Math.max(0, Date.now() - t);
  const m = Math.floor(diff / 60_000);
  if (m < 1) return isEn ? 'now' : 'agora';
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  if (d === 1) return isEn ? 'yesterday' : 'ontem';
  if (d < 7) return `${d}d`;
  return date.toLocaleDateString(isEn ? 'en-US' : 'pt-BR', { day: '2-digit', month: 'short' });
}

function formatGroupTimestamp(iso: string, isEn: boolean): string {
  const d = parseServerDate(iso);
  if (!d) return '';
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  const localeStr = isEn ? 'en-US' : 'pt-BR';
  const time = d.toLocaleTimeString(localeStr, { hour: '2-digit', minute: '2-digit', hour12: isEn });
  if (sameDay) return time;
  return `${d.toLocaleDateString(localeStr, { day: '2-digit', month: 'short' })} · ${time}`;
}
